"use client";

import { useState, useEffect } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { useSelector } from "react-redux";
import { formatISTDateTime } from "../../helper";

const calcAmount = (denom, qty) => Number(denom || 0) * Number(qty || 0);

export default function RakeViewDialog({ open, rake, onClose, refresh }) {
  const token = useSelector((s) => s.loginSlice.token);
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState("OPEN");

  useEffect(() => {
    if (!rake) return;
    setStatus(rake.status || "OPEN");
  }, [rake]);

  const handleCloseRake = async () => {
    try {
      setLoading(true);
      await axiosRequest("PUT", `${apiBaseUrl}/rake`, token, { id: rake._id, status: "CLOSED" });
      setStatus("CLOSED");
      refresh();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  if (!rake) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>Rake Details</Typography>
          <Chip
            label={status}
            color={
              status === "OPEN"    ? "warning" :
              status === "RUNNING" ? "info"    :
              status === "CLOSED"  ? "success" : "default"
            }
            size="small"
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Grid container spacing={2} mt={0.5}>

          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">Table Name</Typography>
              <Typography fontWeight={600}>{rake.table_name || "—"}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">Date & Time</Typography>
              <Typography fontWeight={600}>{formatISTDateTime(rake.date)}</Typography>
            </Box>
          </Grid>

          {rake.created_by?.email && (
            <Grid item xs={12}>
              <Box>
                <Typography variant="caption" color="text.secondary">Submitted By</Typography>
                <Typography fontWeight={600}>{rake.created_by.email}</Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography fontWeight={700} mb={1}>Chips In</Typography>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
              <Grid container sx={{ backgroundColor: "grey.100", px: 2, py: 0.5 }}>
                <Grid item xs={4}><Typography variant="caption" fontWeight={700}>Denomination</Typography></Grid>
                <Grid item xs={4} textAlign="center"><Typography variant="caption" fontWeight={700}>Quantity</Typography></Grid>
                <Grid item xs={4} textAlign="right"><Typography variant="caption" fontWeight={700}>Amount</Typography></Grid>
              </Grid>
              <Divider />
              {(rake.chips_in || []).map((chip, i) => (
                <Grid container key={i} sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider" }} alignItems="center">
                  <Grid item xs={4}>
                    <Typography variant="body2" fontWeight={600}>{chip.denomination}</Typography>
                  </Grid>
                  <Grid item xs={4} textAlign="center">
                    <Typography variant="body2">{chip.quantity}</Typography>
                  </Grid>
                  <Grid item xs={4} textAlign="right">
                    <Typography variant="body2" color={calcAmount(chip.denomination, chip.quantity) > 0 ? "success.main" : "text.disabled"} fontWeight={600}>
                      {calcAmount(chip.denomination, chip.quantity) > 0
                        ? `${calcAmount(chip.denomination, chip.quantity).toLocaleString()}`
                        : "—"}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
              <Grid container sx={{ px: 2, py: 1, backgroundColor: "grey.50" }}>
                <Grid item xs={8}>
                  <Typography fontWeight={700}>Total</Typography>
                </Grid>
                <Grid item xs={4} textAlign="right">
                  <Typography fontWeight={700} color="success.main">
                    {rake.total_in?.toLocaleString() || 0}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box>
              <Typography variant="caption" color="text.secondary">Remarks</Typography>
              <Typography>{rake.remarks || "—"}</Typography>
            </Box>
          </Grid>

        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" startIcon={<CloseIcon />} onClick={onClose}>
          Close
        </Button>
        {status === "OPEN" && (
          <Button variant="outlined" color="error" onClick={handleCloseRake} disabled={loading}>
            {loading ? "Closing..." : "Close Rake"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
